import { Component, Input } from 'ng-metadata/core';
import './display-options.scss';

/**
 * @ngdoc directive
 * @name mwContentStreamDisplayOptions
 * @module mw.components.contentStream
 * @param {object} [display-options] - An object that contains what should be shown in the content stream. This object's structure will be similar to what you have seen before. <br />
 * <code style="white-space: pre-wrap">{
	"avatar" : true,
	"images" : true,
	"displayTags" : true, (tags is the preferred key here but we also support the old displayTags key as well)
	"keywords" : true,
	"hitSentence" : true,
	"openingText" : true,
    "groupOption" : "none",
    "socialEcho" : "false",
    "caseSensitivityOption": 'noMatching',
    "imageSize": 'large'
 * }
 * </code>
 * @description
 * `<mw-content-stream-display-options>` is a component that is responsible for showing the display options in correalation with the content stream
 * @usage
 * <hljs hljs-language="html">
 *  <mw-content-stream-display-options display-options="$ctrl.displayOptions"> </mw-content-stream-display-options>
 * </hljs>
 */
@Component({
    selector: 'mw-content-stream-display-options',
    template: `
        <div class="mw-content-stream-display-options">
            <div layout="column" ng-if="::$ctrl.displaySubMenu('showOptions')">
                <mw-menu-subheader><translate translate="mw-content-stream-display-options__sub-header--show"></translate></mw-menu-subheader>
                <mw-menu-item type="multiselect" is-selected="$ctrl.displayOptions.images === true" ng-if="$ctrl.displayOptions.images !== undefined && !$ctrl.displaySubMenu('imageSize')" class="md-primary" on-click="$ctrl.handleDisplayShowOptionsUpdate('images')"><translate translate="mw-content-stream-display-options__show--images"></translate></mw-menu-item>
                <mw-menu-item type="multiselect" is-selected="$ctrl.displayOptions.avatars === true" ng-if="$ctrl.displayOptions.avatars !== undefined" class="md-primary" on-click="$ctrl.handleDisplayShowOptionsUpdate('avatars')"><translate translate="mw-content-stream-display-options__show--avatars"></translate></mw-menu-item>
                <mw-menu-item type="multiselect" is-selected="$ctrl.displayOptions.openingText === true" ng-if="$ctrl.displayOptions.openingText !== undefined" class="md-primary" on-click="$ctrl.handleDisplayShowOptionsUpdate('openingText')"><translate translate="mw-content-stream-display-options__show--opening-text"></translate></mw-menu-item>
                <mw-menu-item type="multiselect" is-selected="$ctrl.displayOptions.hitSentence === true" ng-if="$ctrl.displayOptions.hitSentence !== undefined" class="md-primary" on-click="$ctrl.handleDisplayShowOptionsUpdate('hitSentence')"><translate translate="mw-content-stream-display-options__show--hit-sentence"></translate></mw-menu-item>
                <mw-menu-item type="multiselect" is-selected="$ctrl.displayOptions.keywords === true" ng-if="$ctrl.displayOptions.keywords !== undefined" class="md-primary" on-click="$ctrl.handleDisplayShowOptionsUpdate('keywords')"><translate translate="mw-content-stream-display-options__show--keywords"></translate></mw-menu-item>
                <mw-menu-item type="multiselect" is-selected="$ctrl.displayOptions.tags === true || $ctrl.displayOptions.displayTags === true" ng-if="$ctrl.displayOptions.tags !== undefined || $ctrl.displayOptions.displayTags !== undefined" class="md-primary" on-click="$ctrl.handleDisplayShowOptionsUpdate('tags')"><translate translate="mw-content-stream-display-options__show--tags"></translate></mw-menu-item>
                <!-- <mw-menu-item type="multiselect" is-selected="$ctrl.displayOptions.socialEcho === true" ng-if="$ctrl.displayOptions.socialEcho !== undefined" class="md-primary" on-click="$ctrl.handleDisplayShowOptionsUpdate('socialEcho')"> Social Echo </mw-menu-item> -->
            </div>
            <div layout="column" ng-if="::$ctrl.displaySubMenu('imageSize')">
                <mw-menu-subheader><translate translate="mw-content-stream-display-options__sub-header--images"></translate></mw-menu-subheader>
                <mw-menu-item type="singleselect" is-selected="$ctrl.displayOptions.imageSize === 'small' && $ctrl.displayOptions.images" on-click="$ctrl.handleImageSizeUpdate('small')"><translate translate="mw-content-stream-display-options__images--small"></translate></mw-menu-item>
                <mw-menu-item type="singleselect" is-selected="$ctrl.displayOptions.imageSize === 'large' && $ctrl.displayOptions.images" on-click="$ctrl.handleImageSizeUpdate('large')"><translate translate="mw-content-stream-display-options__images--large"></translate></mw-menu-item>
                <mw-menu-item type="singleselect" is-selected="!$ctrl.displayOptions.images" on-click="$ctrl.displayOptions.images = false"><translate translate="mw-content-stream-display-options__images--none"></translate></mw-menu-item>
            </div>
            <div layout="column" ng-if="::$ctrl.displaySubMenu('groupOption')">
                <mw-menu-subheader><translate translate="mw-content-stream-display-options__sub-header--grouping"></translate></mw-menu-subheader>
                <mw-menu-item type="singleselect" is-selected="$ctrl.displayOptions.groupOption === 'none'" on-click="$ctrl.handleGroupingOptionUpdate('none')"><translate translate="mw-content-stream-display-options__grouping--none"></translate></mw-menu-item>
                <mw-menu-item type="singleselect" is-selected="$ctrl.displayOptions.groupOption === 'exact'" on-click="$ctrl.handleGroupingOptionUpdate('exact')"><translate translate="mw-content-stream-display-options__grouping--exact"></translate></mw-menu-item>
                <mw-menu-item type="singleselect" is-selected="$ctrl.displayOptions.groupOption === 'close'" on-click="$ctrl.handleGroupingOptionUpdate('close')"><translate translate="mw-content-stream-display-options__grouping--similar"></translate></mw-menu-item>
            </div>
            <div layout="column" ng-if="::$ctrl.displaySubMenu('caseSensitivityOption')">
                <mw-menu-subheader><translate translate="mw-content-stream-display-options__sub-header--case-sensitivity"></translate></mw-menu-subheader>
                <mw-menu-item type="singleselect" is-selected="$ctrl.displayOptions.caseSensitivityOption === 'noMatching'" on-click="$ctrl.handleCaseSensitivityUpdate('noMatching')"><translate translate="mw-content-stream-display-options__case-sensitivity--none"></translate></mw-menu-item>
                <mw-menu-item type="singleselect" is-selected="$ctrl.displayOptions.caseSensitivityOption === 'exactlyMatch'" on-click="$ctrl.handleCaseSensitivityUpdate('exactlyMatch')"><translate translate="mw-content-stream-display-options__case-sensitivity--exact"></translate></mw-menu-item>
                <mw-menu-item type="singleselect" is-selected="$ctrl.displayOptions.caseSensitivityOption === 'onlyMatchCapitals'" on-click="$ctrl.handleCaseSensitivityUpdate('onlyMatchCapitals')"><translate translate="mw-content-stream-display-options__case-sensitivity--caps-only"></translate></mw-menu-item>
            </div>
        </div>
    `
})

export class DisplayOptionsComponent {
    @Input('<') displayOptions: any;

    constructor() {}

    displaySubMenu(submenu) {
        if (submenu === 'showOptions') {
            let showOptions = ['avatars', 'images', 'displayTags', 'tags', 'keywords', 'hitSentence', 'openingText'];

            return showOptions.filter((key) => this.displayOptions.hasOwnProperty(key)).length > 0;
        }

        return this.displayOptions[submenu] !== undefined ? true : false;
    }

    handleDisplayShowOptionsUpdate(option) {
        if (option === 'tags' && this.displayOptions[option] === undefined) {
            this.displayOptions['displayTags'] = !this.displayOptions['displayTags'];
        } else {
            this.displayOptions[option] = !this.displayOptions[option];
        }
    }

    handleImageSizeUpdate(option) {
        this.displayOptions.imageSize = option;
        this.displayOptions.images = true;
    }

    handleGroupingOptionUpdate(option) {
        this.displayOptions.groupOption = option;
    }

    handleCaseSensitivityUpdate(option) {
        this.displayOptions.caseSensitivityOption = option;
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/content-stream/display-options/display-options.component.ts